import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { LoadingComponent } from "Components";
import { JobBox } from "Components/Public/JobBox";
import { saveRedirectAfterLogin } from "Utils";
import { paths } from "Constant/paths";
import { BRAND_NAME, CURRENT_YEAR } from "Constant/constants";

export const CareerJobsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading, totalJobsCount = 0, jobsList = [] } = useOutletContext();

  const baseUrl = process.env.REACT_APP_DEV_URL;
  const pageTitle = t("common.seo.careerJobsList.pageTitle", {
    brandName: BRAND_NAME,
  });
  const canonicalUrl = `${baseUrl}/careers/jobs`;
  const ogImageUrl = `${baseUrl}/assets/images/placeholders/job-banner.jpg`;

  const handleRedirect = () => {
    saveRedirectAfterLogin(paths.common.careerHub);
    navigate(paths.login);
  };

  return (
    <LoadingComponent isLoading={loading}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name="description"
          content={t("common.seo.metaDescription.careerJobsList", {
            brandName: BRAND_NAME,
          })}
        />

        {/* Canonical */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta
          property="og:title"
          content={t("common.seo.careerJobsList.ogTitle", {
            brandName: BRAND_NAME,
          })}
        />
        <meta
          property="og:description"
          content={t("common.seo.careerJobsList.ogDescription", {
            brandName: BRAND_NAME,
          })}
        />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={t("common.seo.careerJobsList.twitterTitle", {
            brandName: BRAND_NAME,
          })}
        />
        <meta
          name="twitter:description"
          content={t("common.seo.careerJobsList.twitterDescription", {
            brandName: BRAND_NAME,
          })}
        />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>

      <section className="pb-10">
        <div className="container-xl">
          <div className="row mb-4">
            <div className="col-12">
              <h1 className="h3 mb-2">Career Opportunities at {BRAND_NAME}</h1>
              <p className="text-muted mb-0">
                Looking for the best internship websites? Read our full guide:{" "}
                <a
                  href="https://blog.plutoedu.com/2025/12/08/best-internship-websites-for-students-in-2025-complete-guide/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Best Internship Websites for Students in {CURRENT_YEAR}
                </a>
              </p>
            </div>
          </div>

          <div className="row justify-content-center gy-3 gx-sm-3">
            {jobsList.length > 0 ? (
              jobsList.map((item, index) => (
                <div
                  className="col-xl-4 col-lg-3 col-md-6 col-sm-6 col-12"
                  key={`${item.gid}-${index}`}
                >
                  <JobBox jobData={item} />
                </div>
              ))
            ) : (
              <div className="d-flex justify-content-center align-items-center p-4">
                No jobs found at the moment. Please check back soon.
              </div>
            )}
          </div>

          {totalJobsCount >= 15 && (
            <div className="row mt-5">
              <div className="col-12 d-flex justify-content-center">
                <button
                  className="btn btn-outline-primary btn-lg"
                  onClick={handleRedirect}
                >
                  View More
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </LoadingComponent>
  );
};
